import { STORAGE_KEY, SUPPORTED_CODES, toCode } from './languages.js'

// ── Tema e idioma: de la persona, en todos sus aparatos ──────────────────────
//
// Los dos viven en DOS sitios y los dos hacen falta:
//
//   · En la CUENTA, que es lo que viaja: se elige oscuro en el portátil y el
//     móvil lo sabe la próxima vez que se abre.
//   · En el NAVEGADOR, como copia. La página se pinta antes de saber quién eres
//     —`index.html` pone el tema antes de cargar nada, `main.js` el idioma antes
//     de montar—, y sin esa copia quien usa el oscuro vería un fogonazo blanco
//     en cada carga hasta que contestase el servidor.
//
// La cuenta manda. Al abrir la app se le pregunta, y lo que diga se aplica y se
// copia aquí. Si la cuenta está vacía —nunca eligió, o eligió antes de que esto
// existiera y solo lo tenía en el navegador— se SUBE lo de aquí en vez de
// pisarlo: la persona no pierde lo que ya había elegido.
//
// Cuando se elige en dos aparatos, gana el último. Es una preferencia, no un
// dato: nadie echa de menos la otra.

/**
 * Pone en esta página lo que dice la cuenta, y sube lo de aquí si la cuenta no
 * dice nada.
 *
 * @param {object} user   el perfil fresco de `/auth/me`
 * @param {object} deps
 * @param {import('vue').Ref<string>} deps.locale   el idioma de vue-i18n
 * @param {(changes: object) => Promise<void>} deps.save   guarda en la cuenta
 * @param {object} deps.theme   `adopt` y `stored` del ui-kit
 *        (`adoptThemePreference`, `storedThemePreference`). Llegan de fuera
 *        para poder probar esto sin cargar los componentes del ui-kit.
 */
export function adoptAccountPreferences(user, { locale, save, theme }) {
  if (!user) return
  const upload = {}

  if (user.theme) theme.adopt(user.theme)
  else if (theme.stored()) upload.theme = theme.stored()

  const here = storedLocale()
  if (user.locale) {
    const code = toCode(user.locale)
    if (locale.value !== code) locale.value = code
    document.documentElement.lang = code
    try { localStorage.setItem(STORAGE_KEY, code) } catch { /* solo la copia */ }
  } else if (here) {
    upload.locale = here
  }

  if (Object.keys(upload).length) return save(upload)
}

/**
 * El guardado en la cuenta, salvo en una demo.
 *
 * La cuenta de una demo es una sola para todos los visitantes y la instancia
 * es de solo lectura: el PATCH se rechaza siempre (DEMO_READ_ONLY), y ese
 * rechazo abría el aviso «En la demo no se guarda nada» nada más entrar, sin
 * que nadie hubiese pulsado Guardar. En una demo la elección se queda en este
 * navegador, que es lo único que tiene sentido para un visitante.
 *
 * @param {(changes: object) => Promise<void>} save   `auth.savePreferences`
 * @param {() => boolean} isDemo   `isDemoInstance` de @general/demo
 */
export function preferenceSaver(save, isDemo) {
  return (changes) => (isDemo() ? undefined : save(changes))
}

function storedLocale() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    return SUPPORTED_CODES.includes(stored) ? stored : null
  } catch { return null }
}
